import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import Row from 'components/Common/Row';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Dropdown from 'components/Inputs/Dropdown';
import Typography from 'kingpin/atoms/Typography';
import Loading from 'components/Loading';
import Tooltip from 'components/Tooltip';
import Button from 'kingpin/atoms/Button';
import STORE from 'store';
import AccountPickerContext from 'contexts/AccountPickerContext';
import ToastContext from 'contexts/ToastContext';

import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import FilterPlates from 'components/FilterPlates';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import Icon from 'kingpin/atoms/Icon';
import filterTypeCheckers from 'components/FilterPlateForm/filterTypeCheckers';

import usePrimaryFieldValueOptions from './usePrimaryFieldValueOptions';
import useAccessToOtherMappedUserIdsOptions from './useAccessToOtherMappedUserIdsOptions';
import useUsersNotMappedToEntityId from './useUsersNotMappedToEntityId';
import Alert from '../../../../../../kingpin/Alert';

const useUserOptions = ({
  mappedUser,
  entityId,
}: {
  mappedUser: MappedUser | undefined;
  entityId: string;
}) => {
  const [selectedUser, setSelectedUser] = useState<
    UserManagement.SignedUpUser | UserManagement.PendingUser | undefined
  >(mappedUser ? mappedUser.user : undefined);
  const { unMappedUsers, isLoading } = useUsersNotMappedToEntityId(entityId);
  const [options, setOptions] = useState<DropdownOption[]>([]);

  const getOptions = useCallback((): DropdownOption[] => {
    const baseUsers = unMappedUsers.map((user) => ({
      label: user.displayName,
      isSelected: selectedUser && selectedUser.id === user.id,
      onSelected: () => {
        setSelectedUser(user);
      },
      key: user.id,
    }));
    const sorted = _.sortBy(baseUsers, 'label');

    if (mappedUser) {
      sorted.push({
        label: mappedUser.user.displayName,
        isSelected: true,
        onSelected: () => {},
        key: mappedUser.user.id,
      });
    }

    return sorted;
  }, [mappedUser, selectedUser, unMappedUsers]);

  useEffect(() => {
    setOptions(getOptions());
  }, [getOptions]);

  return {
    options,
    isLoading: isLoading,
    selectedUser,
  };
};

/**
 * This is for a V3 Mapped user
 *
 * For V2, see {@link EngagementPortalUsers}/PortalMappedUsers/AddMappedUserForm
 */
const AddMappedUserForm = ({
  entity,
  entityAppId,
  entityName,
  close,
  setMappedUsers,
  mappedUser,
  mappedUsers,
  entityLabel,
}: {
  entity: EntityDetails.Entity;
  entityAppId: string;
  setMappedUsers: React.Dispatch<React.SetStateAction<MappedUser[]>>;
  entityName: string;
  close: () => void;
  mappedUser?: MappedUser;
  mappedUsers: MappedUser[];
  entityLabel: string;
}) => {
  const { showToast } = useContext(ToastContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const {
    options: userOptions,
    isLoading: isLoadingUsers,
    selectedUser,
  } = useUserOptions({ mappedUser, entityId: entity.id });
  const [primaryFieldValue, setPrimaryFieldValue] = useState<
    string | undefined
  >(mappedUser ? mappedUser.filterValue : undefined);
  const [accessToOtherMappedUserIds, setAccessToOtherMappedUserIds] = useState<
    string[]
  >(() => {
    if (mappedUser) {
      return mappedUser.accessToOtherMappedUserIds;
    }
    return [];
  });
  const mappedUsersOtherThanCurrentUser = useMemo((): MappedUser[] => {
    if (!mappedUser) {
      return mappedUsers;
    }
    return mappedUsers.filter((mu) => mu.user.id !== mappedUser.user.id);
  }, [mappedUser, mappedUsers]);
  const accessToOtherMappedUserIdsOptions =
    useAccessToOtherMappedUserIdsOptions({
      accessToOtherMappedUserIds,
      setAccessToOtherMappedUserIds,
      mappedUsers: mappedUsersOtherThanCurrentUser,
    });
  const [plates, setPlates] = useState<FilterPlateType[]>(() => {
    if (!mappedUser) {
      return [];
    }

    if (!mappedUser.additionalFilters) {
      return [];
    }

    return mappedUser.additionalFilters;
  });
  const additionalPrimaryFieldValues = useMemo(() => {
    if (!entity) {
      return [];
    }

    return plates.reduce((acc, plate) => {
      if (
        plate.type === 'Fixed' &&
        plate.fixedValue.field === entity.primaryField
      ) {
        const { fixedValue } = plate;
        if (!filterTypeCheckers.isTextFilter(fixedValue)) {
          return acc;
        }

        return _.uniq([...acc, ...fixedValue.keywordValues]);
      }
      return acc;
    }, [] as string[]);
  }, [entity, plates]);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

  const { primaryFieldValueOptions, isLoadingOptions } =
    usePrimaryFieldValueOptions({
      entityId: entity.id,
      primaryFieldValue,
      setPrimaryFieldValue,
      additionalPrimaryFieldValues,
    });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isValid = !!selectedUser && !!primaryFieldValue;

  const getCurrentMappings = useCallback(
    async ({
      selectedUser,
    }: {
      selectedUser: UserManagement.SignedUpUser | UserManagement.PendingUser;
    }): Promise<UserManagement.EngagementContentMapping[]> => {
      const doc = await STORE.userManagement
        .getUserContentSettingsRef({
          accountId: selectedAccountId,
          userId: selectedUser.id,
        })
        .get();
      const data = doc.data();
      if (!data) {
        return [];
      }
      if (data.mode === 'normal') {
        return [];
      }
      return data.mappings;
    },
    [selectedAccountId],
  );

  const onSaveClicked = useCallback(async () => {
    if (!isValid || isLoading || !selectedUser || !primaryFieldValue) {
      return;
    }

    const additionalFilters = plates.filter(filterPlateTypeCheckers.isFixed);
    const currentMappings = await getCurrentMappings({ selectedUser });

    const newContentSettings: UserManagement.ContentSettings = {
      mode: 'engagement',
      mappings: [
        ...currentMappings.filter(
          (m) => m.entityId !== entity.id && m.entityAppId !== entityAppId,
        ),
        {
          primaryFieldValue,
          entityId: entity.id,
          entityAppId,
          additionalFilters,
          accessToOtherMappedUserIds,
        },
      ],
    };

    STORE.userManagement
      .getUserContentSettingsRef({
        accountId: selectedAccountId,
        userId: selectedUser.id,
      })
      .set(newContentSettings)
      .then(() => {
        setMappedUsers((mappedUsers) => {
          if (mappedUsers.some((m) => m.user.id === selectedUser.id)) {
            return mappedUsers.map((u) => {
              if (u.user.id === selectedUser.id) {
                return {
                  ...u,
                  filterValue: primaryFieldValue,
                  additionalFilters,
                  accessToOtherMappedUserIds,
                  entityId: entity.id,
                  entityAppId,
                };
              } else {
                return u;
              }
            });
          } else {
            return [
              ...mappedUsers,
              {
                user: selectedUser,
                filterValue: primaryFieldValue,
                additionalFilters,
                accessToOtherMappedUserIds,
                entityId: entity.id,
                entityAppId,
              },
            ];
          }
        });
        showToast(`${entityLabel} added`);
        setIsLoading(false);
        close();
      });
  }, [
    accessToOtherMappedUserIds,
    close,
    entity.id,
    entityAppId,
    entityLabel,
    getCurrentMappings,
    isLoading,
    isValid,
    plates,
    primaryFieldValue,
    selectedAccountId,
    selectedUser,
    setMappedUsers,
    showToast,
  ]);

  const textFieldModeGreenList = useMemo(() => {
    if (!entity) {
      return undefined;
    }

    return {
      [entity.primaryField]: ['is one of' as 'is one of'],
    };
  }, [entity]);

  const datasetGreenList = useMemo(() => {
    return [entity.entityDataset];
  }, [entity.entityDataset]);

  if (isLoadingUsers || isLoadingOptions) {
    return (
      <Form>
        <FormHeader title={``} onClose={close} />
        <FormContent>
          <Loading />
        </FormContent>
      </Form>
    );
  }

  if (userOptions.length === 0) {
    return (
      <Form>
        <FormHeader
          title={mappedUser ? `Update ${entityLabel}` : `Add ${entityLabel}`}
          onClose={close}
        />
        <FormContent>
          <Alert
            type={'Info'}
            body="We couldn't find any users who have not aleady been mapped to this view"
          />
        </FormContent>
      </Form>
    );
  }

  return (
    <Form>
      <FormHeader
        title={mappedUser ? `Update ${entityLabel}` : `Add ${entityLabel}`}
        onClose={close}
      />
      <FormContent>
        <div style={{ marginBottom: 16 }}>
          <Row centerAlign style={{ gap: 16 }}>
            <div>
              <Row style={{ marginBottom: 4, gap: 4 }} centerAlign>
                <Typography.Body type="Label">User</Typography.Body>
                <Tooltip
                  content={'Please note that only Basic users can be selected'}
                >
                  <Icon icon={'info'} color={'var(--color-grey-4)'} />
                </Tooltip>
              </Row>
              <Dropdown
                options={userOptions}
                placeholder="Select a User"
                isSearchEnabled
                isDisabled={!!mappedUser}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4, opacity: 0 }}>
                <Typography.Body type="Label">x</Typography.Body>
              </div>
              <Icon icon={'arrow-right'} color={'var(--color-icon-tertiary)'} />
            </div>
            <div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body type="Label">{`${entityLabel}`}</Typography.Body>
              </div>
              <Dropdown
                testId={`primary-field-picker`}
                options={primaryFieldValueOptions}
                placeholder={`Select a ${entityLabel}`}
                isSearchEnabled
              />
            </div>
          </Row>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Row spaceBetween>
            <div />
            <Button
              size="Small"
              label="Advanced"
              iconAfter={isAdvancedOpen ? 'chevron-up' : 'chevron-down'}
              type={'Ghost'}
              onClick={() => {
                setIsAdvancedOpen((c) => !c);
              }}
            />
          </Row>
        </div>
        {isAdvancedOpen && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div>
                <Typography.Body type="Label">{`Add More Data`}</Typography.Body>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body
                  type="Annotation"
                  color={'var(--color-grey-3)'}
                >
                  {`Allow access the following ${entityName}s views`}
                </Typography.Body>
              </div>
              <Dropdown
                options={accessToOtherMappedUserIdsOptions}
                placeholder={`Select additional ${entityName}s`}
                isSearchEnabled
                isMulti
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div>
                <Typography.Body type="Label">Filters</Typography.Body>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body
                  type="Annotation"
                  color={'var(--color-grey-3)'}
                >
                  {`Additional filters for ${selectedUser ? `${selectedUser.displayName}'s` : 'this'} view`}
                </Typography.Body>
              </div>
              <FilterPlatesProvider
                drillDowns={plates}
                setDrillDowns={setPlates}
                dataTypes={datasetGreenList}
                textFieldModeGreenList={textFieldModeGreenList}
              >
                <FilterPlates />
              </FilterPlatesProvider>
            </div>
          </>
        )}
      </FormContent>
      <Row centerAlign spaceBetween>
        <div />
        <Button
          type="Primary"
          onClick={onSaveClicked}
          isDisabled={!isValid}
          label={mappedUser ? `Update ${entityLabel}` : `Add ${entityLabel}`}
          size={'Small'}
        />
      </Row>
    </Form>
  );
};

export default AddMappedUserForm;
